import React, { memo, useCallback, useEffect, useState } from "react";
import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import { CALLOUT_KEY } from "modules/utils/Constants";
import colors from "app/colors";
import Toast from "modules/toasts/toast";
import useBurstQuery from "modules/hooks/use-burst-query";
import { AdminUndoTrashIcon, GreenCheckIcon } from "modules/icons";
import {
  InternalAdminUserFragment,
  InternalAdminUserQuery,
  InternalAdminUserQueryVariables,
  InternalAdminEditUserMutation,
  InternalAdminEditUserMutationVariables,
} from "gql/graphql";
import INTERNAL_ADMIN_EDIT_USER_MUTATION from "../_networking/internal-admin-edit-user";
import INTERNAL_ADMIN_USER_QUERY from "../_networking/internal-admin-user";
import useDisplayToast from "modules/hooks/use-display-toast";
import { useOverlay } from "modules/providers/overlay-provider";
import { getUserFullName } from "modules/utils/Functions";

interface IProps {
  userId: string;
}

const InternalAdminReactivateUserModal = ({ userId }: IProps) => {
  const { closeModal } = useOverlay();
  const { displayToast } = useDisplayToast();
  const [user, setUser] = useState<InternalAdminUserFragment | null>();

  const [internalAdminUserAPI] = useBurstQuery<
    InternalAdminUserQuery,
    InternalAdminUserQueryVariables
  >(INTERNAL_ADMIN_USER_QUERY);

  useEffect(() => {
    async function fetchData() {
      if (userId) {
        const response = await internalAdminUserAPI({
          variables: { id: userId },
        });
        if (response.data?.internalAdminUser.length === 1) {
          setUser(response.data.internalAdminUser[0]);
        }

        return;
      }
      setUser(undefined);
    }
    fetchData();
  }, [internalAdminUserAPI, userId]);

  const [internalAdminEditUserMutationAPI] = useMutation<
    InternalAdminEditUserMutation,
    InternalAdminEditUserMutationVariables
  >(INTERNAL_ADMIN_EDIT_USER_MUTATION);

  const onReactivateUserClicked = useCallback(async () => {
    if (!user) return;
    await internalAdminEditUserMutationAPI({
      variables: {
        organizationId: user.organizationId,
        id: user.id,
        input: {
          active: true,
        },
      },
    });
    closeModal();
    displayToast(() => (
      <Toast
        message="User Reactivated"
        leftIcon={<AdminUndoTrashIcon boxSize={5} fill={colors.grayMain} />}
      />
    ));
  }, [user, internalAdminEditUserMutationAPI, closeModal, displayToast]);

  return (
    <Modal isOpen onClose={closeModal} size="sm" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalBody>
          <VStack>
            <GreenCheckIcon boxSize={140} />
            <Text fontSize={20} fontWeight={700}>
              Reactivate User?
            </Text>
            <Text>Are you sure you want to reactivate the user</Text>
            <Text fontWeight={700}>{`${getUserFullName(user)} (${CALLOUT_KEY}${
              user?.callout
            })?`}</Text>
            <HStack>
              <Button variant="cancel" height={8} w={28} onClick={closeModal}>
                No
              </Button>
              <Button height={8} w={28} onClick={onReactivateUserClicked}>
                Yes
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(InternalAdminReactivateUserModal);
